/**
 * coconut-shy.js — GDD 6.2.2. The power-throw stall.
 *
 * Three coconuts sit on metal posts at 1.2 m, 1.5 m and 1.8 m, worth 100, 200
 * and 300 points. Three heavy wooden balls per round, no aim assist, and a
 * gravityScale of 1.2 so the arc feels like real weight (GDD 5.4).
 *
 * The rule that makes the stall: a coconut comes off ONLY on a clean, forceful
 * hit. Everything else wobbles the coconut on its post and scores nothing.
 * "Clean and forceful" is evaluated in `_evaluateImpact` from three conditions —
 * see COCONUT_SHY in constants.js for why the GDD's 8 N threshold alone is not
 * sufficient to express it.
 *
 * A seated coconut is a dynamic body that is re-seated every frame, rather than
 * a fixed or kinematic one. That is deliberate:
 *   - dynamic↔dynamic is the contact pair Rapier reports contact FORCES for,
 *     which is what `physics.onContactForce` (ARCHITECTURE §6) needs;
 *   - re-seating keeps a 1.5 kg ball from simply rolling off a 4.5 cm post;
 *   - the moment it is knocked off we stop re-seating and it falls properly.
 *
 * @module stalls/coconut-shy
 */

import * as THREE from 'three';
import { COCONUT_SHY, EVENTS, HUB } from '../constants.js';
import { StallBase, registerStall } from './framework.js';
import * as factory from '../assets3d/factory.js';
import * as physics from '../core/physics.js';
import { bus } from '../core/events.js';
import { createStallLight } from '../world/lighting.js';
import { haptics } from '../core/haptics.js';

const CFG = COCONUT_SHY;

class CoconutShy extends StallBase {
  constructor(ctx) {
    super(ctx);
    /**
     * @type {Array<{ def: object, handle: object, seat: THREE.Vector3,
     *                knocked: boolean, wobble: object|null }>}
     */
    this.coconuts = [];
    /** Pre-impact ball state, sampled each frame — see `_recordBallMotion`. */
    this._ballMotion = new Map();
    this._ballAge = new Map();
    this._offContact = null;
    this._settleTimer = 0;
    this._lastHit = null;
  }

  // ── Static build ────────────────────────────────────────────────────────

  onBuild() {
    const stopW = HUB.boothWidth - 0.4;
    const far = CFG.posts[CFG.posts.length - 1].distance;

    const floor = new THREE.Mesh(
      new THREE.BoxGeometry(stopW, 0.1, 6.4),
      factory.mat(0x6a5a44, { rough: 1 }),
    );
    floor.receiveShadow = true;
    this.spawn(floor, { x: 0, y: -0.05, z: 2.6 }, {
      type: 'fixed', shape: 'box',
      halfExtents: { x: stopW / 2, y: 0.05, z: 3.2 },
      position: this.localToWorld(0, -0.05, 2.6),
      friction: 0.85, restitution: 0.1,
    });

    const backstop = new THREE.Mesh(
      new THREE.BoxGeometry(stopW, 2.6, 0.1),
      factory.mat(0x6d5f7a, { rough: 0.95 }),
    );
    backstop.receiveShadow = true;
    this.spawn(backstop, { x: 0, y: 1.3, z: far + 1.0 }, {
      type: 'fixed', shape: 'box',
      halfExtents: { x: stopW / 2, y: 1.3, z: 0.05 },
      position: this.localToWorld(0, 1.3, far + 1.0),
      restitution: 0.05, friction: 0.9,
    });

    // Posts are static and outlive the coconuts they carry — GDD 6.2.2 is
    // explicit that the post stays put when the coconut goes.
    for (const def of CFG.posts) {
      const post = factory.createPost(def.height, CFG.postRadius);
      this.spawn(post, { x: def.x, y: def.height / 2, z: def.distance }, {
        type: 'fixed', shape: 'cylinder',
        radius: CFG.postRadius, halfHeight: def.height / 2,
        position: this.localToWorld(def.x, def.height / 2, def.distance),
        friction: 0.7,
      });

      // A shallow cup on top so a seated coconut reads as resting in something.
      const cup = new THREE.Mesh(
        new THREE.CylinderGeometry(CFG.coconutRadius * 0.8, CFG.postRadius * 1.1, 0.04, 12),
        factory.mat(0x7a828d, { rough: 0.5, metal: 0.5 }),
      );
      cup.position.set(def.x, def.height + 0.02, def.distance);
      this.root.add(cup);

      const label = factory.createSign('', {
        w: 0.44, h: 0.17, lines: [`${def.points}`], bg: '#12172a',
      });
      label.position.set(def.x, def.height - 0.22, def.distance - 0.09);
      label.rotation.y = Math.PI;
      this.root.add(label);
    }

    // Matches Tin Can Alley's proven tray height: any lower and the balls are
    // clipped by the bottom of a desktop viewport, so a player cannot see what
    // they are reaching for.
    this.buildTray({ width: 0.8, y: 1.20, z: 0.72 });
    this.buildScoreboard({ y: 2.5, z: 3.2 });

    const light = createStallLight(0xffe0b0, 2.0, 12);
    light.position.set(0, 3.0, 2.4);
    this.root.add(light);
  }

  // ── Round setup ─────────────────────────────────────────────────────────

  setupRound() {
    this.setRoundFormat({ attempts: CFG.ballsPerRound });
    this.coconuts = [];
    this._ballMotion.clear();
    this._ballAge.clear();
    this._settleTimer = 0;
    this._lastHit = null;

    for (let i = 0; i < CFG.posts.length; i++) {
      const def = CFG.posts[i];
      const y = def.height + CFG.coconutRadius;
      const coconut = factory.createCoconut();
      const handle = this.spawn(coconut, { x: def.x, y, z: def.distance }, {
        position: this.localToWorld(def.x, y, def.distance),
        mass: CFG.coconutMass,
        // GDD 6.2.2 lives on this flag: without it Rapier reports no contact
        // force for the coconut and nothing can ever be knocked off.
        events: true,
        contactForceThreshold: 1,
        angularDamping: 0.4,
        linearDamping: 0.1,
      });
      handle.userData.coconutIndex = i;
      handle.userData.points = def.points;
      this.registerTarget(handle, { id: `coconut-${def.key}`, group: def.key });

      this.coconuts.push({
        def,
        handle,
        seat: handle.position().clone(),
        knocked: false,
        wobble: null,
      });
    }

    this.dispense(CFG.ballsPerRound, () => factory.createBall('heavy'), {
      y: 1.27, z: 0.72, spacing: 0.19,
      desc: { ccd: true },
    });

    // Subscribed per round and released in onTeardown, which the manager calls
    // on both exit and play-again — so the pairing stays balanced.
    this._offContact = physics.onContactForce((e) => this._onContactForce(e));
  }

  // ── The knock-off rule (GDD 6.2.2) ──────────────────────────────────────

  /**
   * Resolve a contact participant to the coconut it belongs to, if any.
   * Contact payloads carry either body handle in either slot, and `a`/`b` may
   * be undefined when the collider belongs to something already removed.
   * @param {object} [handle]
   * @returns {object|null}
   * @private
   */
  _entryFor(handle) {
    const index = handle && handle.userData ? handle.userData.coconutIndex : undefined;
    if (index === undefined) return null;
    return this.coconuts[index] || null;
  }

  /**
   * @param {{ a?: object, b?: object, force: number }} e
   * @private
   */
  _onContactForce({ a, b, force }) {
    if (!this.roundActive) return;
    const entry = this._entryFor(a) || this._entryFor(b);
    if (!entry || entry.knocked) return;

    const other = this._entryFor(a) ? b : a;
    const motion = other ? this._ballMotion.get(other.id) : null;
    // Only a thrown ball can dislodge a coconut. A coconut brushing the post,
    // or a ball still sitting in the tray, must not.
    if (!motion) return;

    const toCoconut = entry.handle.position().sub(motion.position);
    if (toCoconut.lengthSq() < 1e-8) return;
    const directness = motion.velocity.clone().normalize().dot(toCoconut.normalize());

    this._evaluateImpact(entry, {
      force,
      speed: motion.speed,
      directness,
      heading: motion.velocity.clone().normalize(),
    });
  }

  /**
   * Decide whether an impact knocks the coconut off or merely wobbles it.
   * The test hook drives this same method, so the threshold a test asserts is
   * the threshold the physics path uses.
   *
   * @param {object} entry
   * @param {{ force: number, speed: number, directness: number,
   *           heading?: THREE.Vector3 }} impact
   * @private
   */
  _evaluateImpact(entry, impact) {
    if (entry.knocked || !this.roundActive) return null;
    const force = impact.force ?? 0;
    const speed = impact.speed ?? 0;
    const directness = impact.directness ?? 1;
    const heading = impact.heading
      || this.localToWorldDirection(new THREE.Vector3(0, 0, 1));

    const clean = force > CFG.knockoffForceN
      && speed >= CFG.minKnockSpeed
      && directness >= CFG.minKnockDirectness;

    this._lastHit = {
      target: entry.handle.userData.targetId,
      force, speed, directness, clean,
    };

    if (clean) this._knockOff(entry, heading, speed);
    else this._startWobble(entry, heading, force, speed);
    return this._lastHit;
  }

  /**
   * Send the coconut off the post. The post itself is a separate fixed body and
   * is untouched (GDD 6.2.2).
   * @private
   */
  _knockOff(entry, heading, speed) {
    entry.knocked = true;
    entry.wobble = null;
    entry.handle.userData.scored = true;

    const kick = heading.clone().setY(0);
    if (kick.lengthSq() < 1e-6) kick.copy(this.localToWorldDirection(new THREE.Vector3(0, 0, 1)));
    kick.normalize().multiplyScalar(Math.min(speed, 12) * 0.55 + CFG.knockImpulse);
    kick.y += 1.6;
    entry.handle.setVelocity(kick);
    entry.handle.setAngularVelocity({
      x: kick.z * 1.4, y: 2.0, z: -kick.x * 1.4,
    });

    this.addScore(entry.def.points, {
      position: entry.handle.position(),
      label: `${entry.def.key} coconut`,
    });
    bus.emit(EVENTS.AUDIO_PLAY, { name: 'coconut-knock', volume: 0.9 });
    haptics.play('right', 'impact', { force: 24 });
  }

  /**
   * A hit that was not clean enough: tilt the coconut and let it spring back.
   * @private
   */
  _startWobble(entry, heading, force, speed) {
    const w = CFG.wobble;
    // Scale the tilt by how hard the hit was, so a near miss visibly rocks the
    // coconut and a graze barely moves it — GDD 6.2.2's "teaches force needed".
    const strength = THREE.MathUtils.clamp(
      Math.max(force / (CFG.knockoffForceN * 4), speed / CFG.minKnockSpeed) * 0.6,
      0.15, 1,
    );
    const axis = new THREE.Vector3(-heading.z, 0, heading.x);
    if (axis.lengthSq() < 1e-6) axis.set(1, 0, 0);
    entry.wobble = {
      t: 0,
      amp: w.maxTilt * strength,
      axis: axis.normalize(),
      lean: heading.clone().setY(0).normalize(),
    };
    bus.emit(EVENTS.AUDIO_PLAY, { name: 'coconut-wobble', volume: 0.5 });
    haptics.play('right', 'miss');
  }

  // ── Per-frame ───────────────────────────────────────────────────────────

  onUpdate(dt) {
    this._reseatCoconuts(dt);
    this._recordBallMotion();
    this._recycleBalls(dt);
    this._checkRoundEnd(dt);
  }

  /**
   * Hold every un-knocked coconut on its post, applying the wobble if one is
   * running. Without this a 1.5 kg sphere balanced on a 4.5 cm post rolls off
   * on the first physics step.
   * @private
   */
  _reseatCoconuts(dt) {
    const w = CFG.wobble;
    for (const entry of this.coconuts) {
      if (entry.knocked || entry.handle.removed) continue;

      let tilt = 0;
      if (entry.wobble) {
        entry.wobble.t += dt;
        const envelope = Math.exp(-w.decay * entry.wobble.t);
        tilt = entry.wobble.amp * envelope * Math.cos(w.frequency * entry.wobble.t);
        if (entry.wobble.amp * envelope < w.minTilt) {
          entry.wobble = null;
          tilt = 0;
        }
      }

      const pos = entry.seat.clone();
      if (entry.wobble) {
        pos.addScaledVector(entry.wobble.lean, Math.sin(tilt) * CFG.coconutRadius * w.lean);
      }
      entry.handle.setPosition(pos);
      entry.handle.setRotation(
        entry.wobble
          ? new THREE.Quaternion().setFromAxisAngle(entry.wobble.axis, tilt)
          : new THREE.Quaternion(),
      );
      entry.handle.freeze();
    }
  }

  /**
   * Sample every live ball's motion once per frame.
   *
   * Contact events are drained inside `physics.step()`, which runs BEFORE this
   * update. So the value read here is the ball's state at the end of the last
   * frame — i.e. its velocity on the way IN, before the bounce reversed it.
   * Reading `handle.velocity()` inside the contact handler would give the
   * post-impact velocity and make every direct hit look like a graze.
   * @private
   */
  _recordBallMotion() {
    for (const handle of this.throwables) {
      if (handle.removed || !handle.userData.thrown) continue;
      const velocity = handle.velocity();
      this._ballMotion.set(handle.id, {
        velocity,
        speed: velocity.length(),
        position: handle.position(),
      });
    }
  }

  /** @private */
  _recycleBalls(dt) {
    for (const handle of this.throwables) {
      if (!handle.userData.thrown || handle.removed) continue;
      const age = (this._ballAge.get(handle.id) ?? 0) + dt;
      this._ballAge.set(handle.id, age);
      if (age >= CFG.ballLifetime) handle.freeze();
    }
  }

  /** @private */
  _checkRoundEnd(dt) {
    if (!this.roundActive) return;
    if (this.coconuts.length > 0 && this.coconuts.every((c) => c.knocked)) {
      this.endRound();
      return;
    }
    if (this.attemptsLeft > 0) return;
    this._settleTimer += dt;
    if (this._settleTimer >= CFG.settleTime) this.endRound();
  }

  // ── Aim assist ──────────────────────────────────────────────────────────

  /**
   * None. GDD 5.4 gives Coconut Shy a 0° cone — the player must actually aim.
   * @returns {null}
   */
  findAimTarget() {
    return null;
  }

  // ── Scoreboard ──────────────────────────────────────────────────────────

  updateScoreboard() {
    if (!this.scoreboard) return;
    const down = this.coconuts.filter((c) => c.knocked).length;
    this.scoreboard.setLines([
      `Score     ${this.score}`,
      `Balls     ${this.attemptsLeft} / ${this.maxAttempts}`,
      `Coconuts  ${down} / ${this.coconuts.length}`,
      `100 / 200 / 300 by height`,
    ]);
  }

  // ── Test automation ─────────────────────────────────────────────────────

  /**
   * Additive hooks reached through `__cc.stalls.action(name, args)`.
   *
   * `hit` injects a synthetic impact into `_evaluateImpact` — the exact method
   * the contact-force path calls — so a test can assert the GDD 6.2.2 threshold
   * without depending on a physics throw landing a specific force.
   *
   * @param {string} name
   * @param {object} args
   */
  testHook(name, args = {}) {
    switch (name) {
      case 'hit': {
        const entry = this.coconuts.find(
          (c) => c.handle.userData.targetId === args.targetId,
        ) || this.coconuts[args.index ?? 0];
        if (!entry) return null;
        return this._evaluateImpact(entry, {
          force: args.force ?? 20,
          speed: args.speed ?? 8,
          directness: args.directness ?? 1,
        });
      }
      case 'state':
        return {
          lastHit: this._lastHit,
          coconuts: this.coconuts.map((c) => ({
            id: c.handle.userData.targetId,
            key: c.def.key,
            points: c.def.points,
            height: c.def.height,
            knocked: c.knocked,
            wobbling: !!c.wobble,
          })),
        };
      default:
        return null;
    }
  }

  onTeardown() {
    if (this._offContact) this._offContact();
    this._offContact = null;
    this.coconuts = [];
    this._ballMotion.clear();
    this._ballAge.clear();
    this._lastHit = null;
  }
}

registerStall({
  id: 'coconut-shy',
  name: 'Coconut Shy',
  cost: CFG.cost,
  create: (ctx) => new CoconutShy(ctx),
});

export { CoconutShy };
